/**
 * The static table, pre-rendered once to an offscreen canvas (TBL-001).
 *
 * Cloth, rails, diamonds and pocket cut-outs never change during a shot, so they are
 * drawn once and blitted each frame. Only the balls are redrawn (PRD §5).
 *
 * Every colour comes from a design token — a component that picks its own colour
 * silently breaks themes (CLAUDE.md §6).
 */
import type { Pocket, TableGeometry } from '@potted/sim';
import { CUSHION_WIDTH_M, POCKET_RING_SCALE, frameWidthM, pocketHole } from './frame.ts';
import type { Viewport } from './viewport.ts';

/** Read a token once per re-render, not per frame. */
function token(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export interface TableLayer {
  readonly canvas: HTMLCanvasElement;
}

export function renderTableLayer(geometry: TableGeometry, viewport: Viewport): TableLayer {
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.cssWidth * viewport.dpr);
  canvas.height = Math.ceil(viewport.cssHeight * viewport.dpr);

  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('renderTableLayer: could not get a 2D context');
  ctx.scale(viewport.dpr, viewport.dpr);

  const { spec } = geometry;
  const sx = (xM: number, yM: number) => viewport.toScreenX(xM, yM);
  const sy = (xM: number, yM: number) => viewport.toScreenY(xM, yM);

  // Wide enough to hold the pocket rings inside the wood (frame.ts, L36).
  const railPx = viewport.lengthPx(frameWidthM(geometry));
  // The drawn box comes from the viewport, which knows which way round the table is.
  // Deriving it here from `widthM`/`heightM` would silently assume portrait (UI-003).
  const { left, top, width, height } = viewport.tableRect();

  /**
   * A point on a rail, nudged `px` out of the table along the rail's outward normal.
   *
   * The normal is given in TABLE space and converted, so a diamond sits outside the
   * cloth in either orientation rather than in a direction that only happens to be
   * right when the table is rotated.
   */
  const outward = (
    xM: number,
    yM: number,
    outX: number,
    outY: number,
    px: number,
  ): { x: number; y: number } => {
    const d = viewport.screenDelta(outX, outY);
    const length = Math.sqrt(d.x * d.x + d.y * d.y);
    if (length === 0) return { x: sx(xM, yM), y: sy(xM, yM) };
    return { x: sx(xM, yM) + (d.x / length) * px, y: sy(xM, yM) + (d.y / length) * px };
  };

  // --- Rails -----------------------------------------------------------------
  ctx.fillStyle = token('--color-rail');
  // Each corner is a quarter circle centred on its corner pocket, so the rail wraps
  // round the pocket instead of the pocket poking out of a square corner.
  roundRect(ctx, left - railPx, top - railPx, width + railPx * 2, height + railPx * 2, railPx);
  ctx.fill();

  ctx.strokeStyle = token('--color-rail-shadow');
  ctx.lineWidth = 1;
  ctx.stroke();

  // --- Cloth -----------------------------------------------------------------
  const cloth = ctx.createLinearGradient(left, top, left + width, top + height);
  cloth.addColorStop(0, token('--color-cloth-sheen'));
  cloth.addColorStop(0.55, token('--color-cloth'));
  cloth.addColorStop(1, token('--color-cloth-edge'));
  ctx.fillStyle = cloth;
  ctx.fillRect(left, top, width, height);

  /*
   * Clip to the far side of a pocket's mouth line. A ball drops the moment its centre
   * crosses that line (PHY-009), so nothing that reads as "hole" may be drawn on the
   * near side of it. Before PHY-009 a side pocket's hole was a full circle, and half of
   * it sat on the cloth.
   */
  const clipBeyondMouth = (pocket: Pocket): void => {
    const { mouthCentre: m, outward: o } = pocket;
    // Along the mouth line, then out of the table; 1 m dwarfs any pocket.
    const tx = -o.yM;
    const ty = o.xM;
    const box: [number, number][] = [
      [m.xM + tx, m.yM + ty],
      [m.xM + tx + o.xM, m.yM + ty + o.yM],
      [m.xM - tx + o.xM, m.yM - ty + o.yM],
      [m.xM - tx, m.yM - ty],
    ];
    ctx.beginPath();
    for (const [xM, yM] of box) ctx.lineTo(sx(xM, yM), sy(xM, yM));
    ctx.closePath();
    ctx.clip();
  };

  // --- Pocket rings ------------------------------------------------------------
  // Under the cushions, so the cushions' angled ends show across them (TBL-002).
  ctx.fillStyle = token('--color-pocket-hardware');
  for (const pocket of geometry.pockets) {
    const hole = pocketHole(pocket);
    ctx.save();
    clipBeyondMouth(pocket);
    ctx.beginPath();
    ctx.arc(
      sx(hole.xM, hole.yM),
      sy(hole.xM, hole.yM),
      viewport.lengthPx(hole.radiusM * POCKET_RING_SCALE),
      0,
      Math.PI * 2,
    );
    ctx.fill();
    ctx.restore();
  }

  // --- Cushions (TBL-002) ------------------------------------------------------
  // The rubber strip between cloth and wood, one per cushion segment the physics
  // collides with. Its nose IS the cushion line, and each end is cut back along that
  // pocket's jaw wall, so the angled face into a pocket is drawn exactly where a ball
  // meets it. That angled cut is what makes a pocket look set into the cushion.
  const jaws = geometry.pockets.flatMap((pocket) => pocket.jaws);
  const near = (a: number, b: number): boolean => Math.abs(a - b) < 1e-9;

  /** Where the cushion's back edge meets the jaw wall that starts at `tip`. */
  const cutBack = (
    tipX: number,
    tipY: number,
    outX: number,
    outY: number,
  ): { xM: number; yM: number } => {
    const wall = jaws.find((j) => near(j.from.xM, tipX) && near(j.from.yM, tipY));
    let dx = outX;
    let dy = outY;
    if (wall !== undefined) {
      const wx = wall.to.xM - wall.from.xM;
      const wy = wall.to.yM - wall.from.yM;
      const length = Math.sqrt(wx * wx + wy * wy);
      // How far the wall goes outward per metre along it; guard a wall along the rail.
      const outward = length === 0 ? 0 : (wx * outX + wy * outY) / length;
      if (outward > 0.05) {
        dx = wx / length / outward;
        dy = wy / length / outward;
      }
    }
    return { xM: tipX + dx * CUSHION_WIDTH_M, yM: tipY + dy * CUSHION_WIDTH_M };
  };

  for (const cushion of geometry.cushions) {
    const outX = -cushion.nx;
    const outY = -cushion.ny;
    const a = cushion.from;
    const b = cushion.to;
    const backA = cutBack(a.xM, a.yM, outX, outY);
    const backB = cutBack(b.xM, b.yM, outX, outY);

    ctx.beginPath();
    ctx.moveTo(sx(a.xM, a.yM), sy(a.xM, a.yM));
    ctx.lineTo(sx(b.xM, b.yM), sy(b.xM, b.yM));
    ctx.lineTo(sx(backB.xM, backB.yM), sy(backB.xM, backB.yM));
    ctx.lineTo(sx(backA.xM, backA.yM), sy(backA.xM, backA.yM));
    ctx.closePath();
    ctx.fillStyle = token('--color-cushion');
    ctx.fill();
    // Shade the back half, so the strip reads as sloping down to the wood.
    const shade = ctx.createLinearGradient(
      sx(a.xM, a.yM),
      sy(a.xM, a.yM),
      sx(a.xM + outX * CUSHION_WIDTH_M, a.yM + outY * CUSHION_WIDTH_M),
      sy(a.xM + outX * CUSHION_WIDTH_M, a.yM + outY * CUSHION_WIDTH_M),
    );
    shade.addColorStop(0.35, 'transparent');
    shade.addColorStop(1, token('--color-cushion-shade'));
    ctx.fillStyle = shade;
    ctx.fill();

    // The nose, lit: the line a ball actually touches.
    ctx.beginPath();
    ctx.moveTo(sx(a.xM, a.yM), sy(a.xM, a.yM));
    ctx.lineTo(sx(b.xM, b.yM), sy(b.xM, b.yM));
    ctx.strokeStyle = token('--color-cushion-nose');
    ctx.lineWidth = Math.max(1, viewport.lengthPx(0.003));
    ctx.stroke();
  }

  // --- Diamond sights, inlaid in the rails -----------------------------------
  ctx.fillStyle = token('--color-diamond');
  const diamond = Math.max(2, railPx * 0.16);
  // Centred in the wood beyond the cushion (TBL-002).
  const cushionPx = viewport.lengthPx(CUSHION_WIDTH_M);
  const inset = cushionPx + (railPx - cushionPx) / 2;
  // Long rails carry 8 spaces = 7 sights, minus the one hidden by the side pocket.
  for (let i = 1; i <= 7; i++) {
    if (i === 4) continue; // side pocket sits here
    const xM = (spec.widthM * i) / 8;
    const low = outward(xM, 0, 0, -1, inset);
    const high = outward(xM, spec.heightM, 0, 1, inset);
    drawDiamond(ctx, low.x, low.y, diamond);
    drawDiamond(ctx, high.x, high.y, diamond);
  }
  // Short rails carry 4 spaces = 3 sights.
  for (let i = 1; i <= 3; i++) {
    const yM = (spec.heightM * i) / 4;
    const near = outward(0, yM, -1, 0, inset);
    const far = outward(spec.widthM, yM, 1, 0, inset);
    drawDiamond(ctx, near.x, near.y, diamond);
    drawDiamond(ctx, far.x, far.y, diamond);
  }

  // --- Pockets ---------------------------------------------------------------
  // Drawn from the same geometry the physics uses, so what you see is what a ball
  // will actually meet: the felt running on between the two jaw walls and curving over
  // the rim of the hole, which sits set back in the rail (frame.ts). Modelled on the
  // owner's 8 Ball Pool reference: the felt stays light right up to a lit lip, and the
  // shadow is only a thin band just inside it.
  for (const pocket of geometry.pockets) {
    const hole = pocketHole(pocket);
    const hx = sx(hole.xM, hole.yM);
    const hy = sy(hole.xM, hole.yM);
    const holePx = viewport.lengthPx(hole.radiusM);
    ctx.save();
    clipBeyondMouth(pocket);

    // The throat between the jaws, felt-covered like the bed of the table.
    const [tipA, tipB] = pocket.mouthTips;
    const [jawA, jawB] = pocket.jaws;
    ctx.beginPath();
    ctx.moveTo(sx(tipA.xM, tipA.yM), sy(tipA.xM, tipA.yM));
    ctx.lineTo(sx(jawA.to.xM, jawA.to.yM), sy(jawA.to.xM, jawA.to.yM));
    ctx.lineTo(sx(jawB.to.xM, jawB.to.yM), sy(jawB.to.xM, jawB.to.yM));
    ctx.lineTo(sx(tipB.xM, tipB.yM), sy(tipB.xM, tipB.yM));
    ctx.closePath();
    ctx.fillStyle = cloth;
    ctx.fill();

    // The hole, lighter at its rim, where the felt rolls over and down into it.
    const depth = ctx.createRadialGradient(hx, hy, holePx * 0.55, hx, hy, holePx);
    depth.addColorStop(0, token('--color-pocket'));
    depth.addColorStop(1, token('--color-pocket-rim'));
    ctx.fillStyle = depth;
    ctx.beginPath();
    ctx.arc(hx, hy, holePx, 0, Math.PI * 2);
    ctx.fill();

    // The lip catches the light, but only where the felt meets the hole: clipped to
    // the throat, so it never draws across the wood or the cushions.
    ctx.beginPath();
    ctx.moveTo(sx(tipA.xM, tipA.yM), sy(tipA.xM, tipA.yM));
    ctx.lineTo(sx(jawA.to.xM, jawA.to.yM), sy(jawA.to.xM, jawA.to.yM));
    ctx.lineTo(sx(jawB.to.xM, jawB.to.yM), sy(jawB.to.xM, jawB.to.yM));
    ctx.lineTo(sx(tipB.xM, tipB.yM), sy(tipB.xM, tipB.yM));
    ctx.closePath();
    ctx.clip();
    ctx.strokeStyle = token('--color-pocket-lip');
    ctx.lineWidth = Math.max(1, viewport.lengthPx(0.004));
    ctx.beginPath();
    ctx.arc(hx, hy, holePx, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  return { canvas };
}

function drawDiamond(ctx: CanvasRenderingContext2D, x: number, y: number, size: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - size);
  ctx.lineTo(x + size, y);
  ctx.lineTo(x, y + size);
  ctx.lineTo(x - size, y);
  ctx.closePath();
  ctx.fill();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
